import type { CSSProperties } from 'react';

export const PROVIDER_COLOURS = 6;

export function providerToken(color: number): string {
  const index = ((Math.round(color) - 1) % PROVIDER_COLOURS + PROVIDER_COLOURS) % PROVIDER_COLOURS + 1;
  return index === 1 ? '--primary' : `--chart-${index}`;
}

export function providerStyle(color: number): CSSProperties {
  const token = providerToken(color);
  return { '--provider': `var(${token})` } as CSSProperties;
}

export function avatarStyle(color: number): CSSProperties {
  const token = providerToken(color);
  return { backgroundColor: `hsl(var(${token}) / 0.15)`, color: `hsl(var(${token}))` };
}

export interface ChipLook {
  tone: 'provider' | 'arrived' | 'consulting' | 'completed' | 'no_show' | 'cancelled';
  className: string;
  pulse: boolean;
  dimmed: boolean;
  urgent: boolean;
  feeAlert: boolean;
}

const BASE = 'rounded-md border-s-[3px] shadow-sm';

export function chipLook(appointment: { status: string; priority?: string | null; feeAlert?: string | null }): ChipLook {
  const urgent = appointment.priority === 'urgent';
  const feeAlert = Boolean(appointment.feeAlert);
  switch (appointment.status) {
    case 'arrived':
      return { tone: 'arrived', className: `${BASE} border-chart-2 bg-chart-2/15 text-foreground`, pulse: false, dimmed: false, urgent, feeAlert };
    case 'in_consultation':
      return { tone: 'consulting', className: `${BASE} border-primary bg-primary/15 text-foreground`, pulse: true, dimmed: false, urgent, feeAlert };
    case 'completed':
      return { tone: 'completed', className: `${BASE} border-success bg-success/10 text-muted-foreground opacity-60`, pulse: false, dimmed: true, urgent: false, feeAlert };
    case 'no_show':
      return { tone: 'no_show', className: `${BASE} border-warning bg-warning/15 text-foreground`, pulse: false, dimmed: false, urgent: false, feeAlert };
    case 'cancelled':
      return { tone: 'cancelled', className: `${BASE} border-muted-foreground/40 bg-muted/60 text-muted-foreground line-through opacity-50`, pulse: false, dimmed: true, urgent: false, feeAlert: false };
    default:
      return {
        tone: 'provider',
        className: `${BASE} border-[hsl(var(--provider))] bg-[hsl(var(--provider)/0.12)] text-foreground`,
        pulse: false,
        dimmed: false,
        urgent,
        feeAlert,
      };
  }
}

export function sessionBackground(kind: 'session' | 'outside' | 'leave'): CSSProperties {
  if (kind === 'session') return { backgroundColor: 'hsl(var(--card))' };
  if (kind === 'leave') return { backgroundColor: 'hsl(var(--destructive) / 0.1)', borderInlineStart: '2px solid hsl(var(--destructive) / 0.5)' };
  return { backgroundImage: 'repeating-linear-gradient(135deg, hsl(var(--muted) / 0.4) 0 6px, transparent 6px 12px)' };
}

export function isShownOnCalendar(status: string): boolean {
  return status !== 'cancelled';
}
